import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RestaurantIcon from '@mui/icons-material/Restaurant';

const ForWhomSection = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const targetAudiences = [
    {
      icon: <StorefrontIcon sx={{ fontSize: 48 }} />,
      title: t('small_business_title'),
      description: t('small_business_desc'),
      color: theme.palette.primary.main,
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 48 }} />,
      title: t('large_enterprise_title'),
      description: t('large_enterprise_desc'),
      color: theme.palette.secondary.main,
    },
    {
      icon: <WarehouseIcon sx={{ fontSize: 48 }} />,
      title: t('warehouses_title'),
      description: t('warehouses_desc'),
      color: theme.palette.success.main,
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 48 }} />,
      title: t('accountants_title'),
      description: t('accountants_desc'),
      color: theme.palette.info.main,
    },
    {
      icon: <RestaurantIcon sx={{ fontSize: 48 }} />,
      title: t('hospitality_title'),
      description: t('hospitality_desc'),
      color: theme.palette.warning.main,
    },
    {
      icon: <LocalShippingIcon sx={{ fontSize: 48 }} />,
      title: t('distributors_title'),
      description: t('distributors_desc'),
      color: theme.palette.error.main,
    },
  ];

  return (
    <Box
      id="for-whom"
      sx={{
        py: 10,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%),
                       radial-gradient(circle at 80% 80%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.9rem',
              mb: 2,
              display: 'block',
            }}
          >
            {t('for_whom_section_label')}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.75rem' },
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('for_whom_section_title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.8,
              fontWeight: 400,
            }}
          >
            {t('for_whom_section_subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {targetAudiences.map((audience, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: `1px solid ${alpha(audience.color, 0.1)}`,
                  background: theme.palette.mode === 'dark' 
                    ? theme.palette.background.paper 
                    : '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${audience.color} 0%, ${alpha(audience.color, 0.6)} 100%)`,
                  },
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: `0 20px 40px ${alpha(audience.color, 0.2)}`,
                    border: `1px solid ${alpha(audience.color, 0.3)}`,
                    '& .icon-box': {
                      transform: 'scale(1.1) rotate(5deg)',
                      background: `linear-gradient(135deg, ${audience.color} 0%, ${alpha(audience.color, 0.8)} 100%)`,
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    className="icon-box"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${alpha(audience.color, 0.1)} 0%, ${alpha(audience.color, 0.05)} 100%)`,
                      color: audience.color,
                      mx: 'auto',
                      mb: 3,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {audience.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {audience.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.8,
                    }}
                  >
                    {audience.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ForWhomSection;
